import { sharedSchema } from "@shared-database/pgSchema";
import { File } from "@shared-database/models/file";
import { DocumentIntelligenceStatusEnumValues } from "@shared-database/enums";
import {
  leoEnum,
  leoNotNullForeignID,
  leoID,
  timestampColumns,
} from "@shared-database/utils";
import type { InferInsertModel, InferSelectModel } from "drizzle-orm";
import { integer, jsonb, text, index } from "drizzle-orm/pg-core";

export const DocumentIntelligenceStatusEnum = leoEnum(
  "DocumentIntelligenceStatus",
  DocumentIntelligenceStatusEnumValues,
);

/**
 * Canonical Document Intelligence extraction for a file.
 *
 * This table owns reusable text/spatial extraction metadata only. Chunk RAG
 * and profile GraphRAG store their processing state separately so the same DI
 * result can be reused by profile uploads, SharePoint profile files, and
 * chat-uploaded files without carrying graph-specific state.
 */
export const DocumentIntelligence = sharedSchema.table(
  "DocumentIntelligence",
  {
    // Mixins
    ...timestampColumns,

    // References
    fileId: leoNotNullForeignID("fileId").references(() => File.id, {
      onDelete: "cascade",
    }),

    // Properties
    id: leoID("id"),
    endpointVersion: text("endpointVersion").notNull(),
    model: text("model").notNull(),
    result: jsonb("result").notNull(),
    charCount: integer("charCount").notNull(),
    status: DocumentIntelligenceStatusEnum("status").notNull(),
    /** Blob name for spatial data (word positions/polygons) used for citation highlighting */
    spatialDataBlobName: text("spatialDataBlobName"),
  },
  (table) => [index().on(table.fileId), index().on(table.status)],
);

// Types
export type DocumentIntelligenceType = InferSelectModel<
  typeof DocumentIntelligence
>;
export type DocumentIntelligenceInsertType = InferInsertModel<
  typeof DocumentIntelligence
>;
